/**
 * Graffiti tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const graffitiTagsPack: ToilePack = {
    slug: 'graffiti-tags',
    category: 'illustration',
    name: 'Graffiti tags',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'marker', label: 'Marker tag', subject: 'the tag "NOVA" written fast in a single connected marker line, a long underline swoosh and a small star at the end'},
        {key: 'crown', label: 'With crown', subject: 'the tag "KING" in tall leaning letters with a three-point crown above the K and two drips falling from the G'},
        {key: 'sticker', label: 'Sticker tag', subject: 'the tag "ZEKE" on a "HELLO MY NAME IS" name-badge sticker, the badge text printed in the header and the tag filling the white area'},
    ],
    styles: [
        {key: 'blackbook', label: 'Black book', style: 'a quick black fineliner tag on notebook paper, one fast confident stroke with a slight ink bleed', palette: 'Black ink only on a white paper background.', framing: 'The tag is centered with a wide margin of empty paper.'},
        {key: 'paint', label: 'Paint marker', style: 'a thick paint-marker tag in flat illustration with small drips and a clean hard edge', palette: 'Silver-white marker lines on a solid dark gray background.', framing: 'The tag is centered, filling most of the frame width.'},
        {key: 'duotone', label: 'Two colors', style: 'a bold two-color marker tag with a flat fill, a hard black outline and one white highlight stroke', palette: 'Red and yellow with black outlines on a white background.', framing: 'The tag is centered with a narrow margin.'},
    ],
    directions: ['your name', 'gold and black', 'with a heart'],
    directionHint: 'try: your name',
    seo: {
        title: 'Graffiti Tags: Names with a Crown, Underline or Badge',
        metaDescription: 'Nine graffiti tags to copy for a sketchbook or a sticker, each name spelled exactly, in marker, fineliner and two colors.',
        h1: 'Graffiti tags: a marker tag, a crowned tag, a sticker tag',
        tagline: 'Nine tags of three names, each written fast in one line.',
        intro: "A graffiti tag is a writer's signature: a name done fast in one line, with a crown, a star or an underline. The nine sheets write NOVA with an underline swoosh, KING with a three-point crown, and ZEKE on a name-badge sticker. Each comes as a black fineliner tag, a paint-marker tag, or a bold two-color marker tag. Ask for your name, gold and black or a heart.",
        useCases: [
            'a sketchbook tag practice',
            'a sticker design',
            'a signature idea for a notebook',
            'a tag to copy and study',
        ],
        faq: [
            {q: 'What is a graffiti tag?', a: "A writer's signature: a name done fast in one line, often with a crown, a star or an underline."},
            {q: 'Are the names spelled exactly?', a: 'Yes. NOVA, KING and ZEKE are spelled as written. A name you type is a request, not a guaranteed result.'},
            {q: 'What is the sticker tag?', a: 'The name ZEKE on a HELLO MY NAME IS badge sticker.'},
            {q: 'What are the three treatments?', a: 'Black fineliner is a quick single stroke on notebook paper. Paint marker is a thick tag with small drips. Two colors is a bold marker tag with a flat fill and a hard black outline.'},
        ],
    },
}
